import type { SendersFacets, getSenderOwnersOutput } from "@/api/custom_types";
import { facetCounter, toFacetFilterOptions } from "@/components";
import { useAutoApplyFilters } from "@/hooks/useAutoApplyFilters";
import type { FacetCounts } from "@/hooks/useFacetFilters";
import { useTranslation } from "@/hooks/useTranslation";
import {
	getSenderPriorityTranslation,
	useSenderStatusTranslator,
} from "@/utils/translations";
import {
	Card,
	CardContent,
	type FieldConfig,
	type FilterConfig,
	GenericFilters,
} from "@st-marketplace/ui";
import { useMemo } from "react";
import { z } from "zod";

const filterSchema = z.object({
	email: z.string().optional(),
	status: z.array(z.string()),
	groups: z.array(z.string()),
	owner: z.string().optional(),
	priority: z.array(z.string()),
	cooldown: z.string().optional(),
});
export type FilterFormData = z.infer<typeof filterSchema>;

interface SendersTableFilters {
	email: string;
	status: string[];
	groups: string[];
	owner: string;
	priority: string[];
	cooldown: string;
}

function toTableFilters(values?: FilterFormData): SendersTableFilters {
	return {
		email: values?.email ?? "",
		status: values?.status ? [...values.status] : [],
		groups: values?.groups ? [...values.groups] : [],
		owner: values?.owner ?? "",
		priority: values?.priority ? [...values.priority] : [],
		cooldown: values?.cooldown ?? "",
	};
}

interface FilterFormProps {
	onSearch: (values: FilterFormData) => void;
	defaultValues?: FilterFormData;
	facetCounts: FacetCounts<SendersFacets>;
	/** Admin only: whose senders. Leave undefined to hide the filter. */
	owners?: getSenderOwnersOutput;
}

export const SendersFilterForm: React.FC<FilterFormProps> = ({
	onSearch,
	defaultValues,
	facetCounts,
	owners,
}) => {
	const { t } = useTranslation();

	const { filters, onFiltersChange, clearFilters } =
		useAutoApplyFilters<SendersTableFilters>({
			initialFilters: toTableFilters(defaultValues),
			onApply: onSearch,
			textFilterKeys: ["email"],
		});

	const translateStatus = useSenderStatusTranslator(t);

	const statusOptions = useMemo(
		() =>
			toFacetFilterOptions(
				facetCounts.statuses,
				(status) => translateStatus(status).text as string,
			),
		[facetCounts.statuses, translateStatus],
	);

	const senderPriorityOptions = useMemo(
		() =>
			toFacetFilterOptions(
				facetCounts.senders_priorities,
				(status) => getSenderPriorityTranslation(status, t).text as string,
			),
		[facetCounts.senders_priorities, t],
	);

	const cooldownOptions = useMemo(() => {
		const counts = new Map(
			(facetCounts.snipe_cooldown ?? []).map((f) => [f.value, f]),
		);
		return (
			[
				["ready", false, t("sender.cooldownReady")],
				["active", true, t("sender.cooldownActive")],
			] as const
		).map(([value, onCooldown, label]) => {
			const facet = counts.get(onCooldown);
			return {
				value,
				label,
				counter: facetCounter(facet?.filtered ?? 0, facet?.total ?? 0),
			};
		});
	}, [facetCounts.snipe_cooldown, t]);

	const groupOptions = useMemo(
		() => toFacetFilterOptions(facetCounts.group_names),
		[facetCounts.group_names],
	);

	const ownerOptions = useMemo(
		() =>
			owners?.map((owner) => ({
				value: owner.user_id,
				label: owner.display_name,
				counter: String(owner.count),
			})),
		[owners],
	);

	const config = useMemo<FilterConfig<SendersTableFilters>>(() => {
		const fields: FieldConfig<SendersTableFilters>[] = [
			{
				key: "email",
				type: "search",
				placeholder: t("sender.filterEmail"),
			},
			{
				key: "status",
				type: "multi-select",
				placeholder: t("sender.filterStatus"),
				options: statusOptions,
				searchable: true,
			},
			{
				key: "priority",
				type: "multi-select",
				placeholder: t("sender.filterPriority"),
				options: senderPriorityOptions,
				searchable: true,
			},
			{
				key: "cooldown",
				type: "select",
				placeholder: t("sender.filterCooldown"),
				options: cooldownOptions,
			},
			{
				key: "groups",
				type: "multi-select",
				placeholder: t("sender.filterGroupName"),
				options: groupOptions,
				searchable: true,
			},
		];
		if (ownerOptions) {
			fields.unshift({
				key: "owner",
				type: "select",
				placeholder: t("sender.filterOwner"),
				options: ownerOptions,
				searchable: true,
			});
		}

		return {
			fields,
			searchDebounceMs: 0,
			showClearButton: true,
		};
	}, [
		t,
		statusOptions,
		senderPriorityOptions,
		cooldownOptions,
		groupOptions,
		ownerOptions,
	]);

	const handleClearFilters = () => {
		clearFilters(toTableFilters());
	};

	return (
		<Card className="w-full">
			<CardContent>
				<GenericFilters
					config={config}
					filters={filters}
					onFiltersChange={onFiltersChange}
					onClearFilters={handleClearFilters}
				/>
			</CardContent>
		</Card>
	);
};
